/**
 * Layer 3 — more about tonight (vanilla Home).
 * Binds server /ai/* items Soft/Live. Do not paint those paths, ports, or MQTT.
 */
import { escapeHtml } from "./dom.js";
import { operatorSay } from "../cmc/operator-say.js";
import { decisionSurfaceActionsHtml } from "./decision-surface.js";
import { maicActionWorkbenchHtml, maicFieldsFromAiItems } from "./response-loop-workbench.js";
import { capabilityRegistrySoftHtml } from "./capability-registry-facade.js";

/**
 * @param {{ items?: object[], settings?: object, aiOn?: boolean, softLanes?: object|null } | null} aiPack
 * @param {{ isAdmin?: boolean }} [opts]
 */
export function buildAiContextDeepHtml(aiPack, opts = {}) {
  const isAdmin = !!opts.isAdmin;
  const pack = aiPack && typeof aiPack === "object" ? aiPack : {};
  const aiOn = pack.aiOn !== false && isAdmin;
  const fields = maicFieldsFromAiItems(pack.items);
  const waitingN = fields.waitingN || 0;
  const leadId = String(fields.leadId || "").trim();
  const modelUsed = String(fields.modelUsed || "").trim();
  const modelTier = String(fields.modelTier || "").trim().toLowerCase();
  const modelLive =
    modelTier === "live"
    || /^rules\+/.test(modelUsed)
    || (/qwen|glm|ollama|live/i.test(modelUsed) && !/soft/i.test(modelUsed));
  const pulse = waitingN > 0 ? "interrupt" : "watching";
  const pulseLine = waitingN > 0
    ? (waitingN === 1
      ? "Needs you — one plan is waiting below"
      : `${waitingN} plans need you — the next step is below`)
    : (aiOn
      ? "Watching — the Watch is on the loop"
      : "Ready — the Watch opens when policy is on");

  const modelBadge = modelUsed
    ? `<span class="badge ${modelLive ? "online" : "neutral"}" title="Provider can swap on the server">On · ${escapeHtml(operatorSay(modelUsed, modelLive ? "Live" : "Preview"))}</span>`
    : `<span class="badge neutral" title="Rules path">Rules · Preview</span>`;

  const whyLine = fields.reasonLine
    ? operatorSay(fields.reasonLine, "Rules keep the score. You keep the gate.")
    : (waitingN > 0
      ? "Rules keep the score. A short why may appear. You keep the gate."
      : "Quiet — a why appears when a plan waits");

  const advertised = Array.isArray(pack.decision_actions) ? pack.decision_actions : [];
  const decisionBlock = advertised.length && leadId
    ? `<div class="croc-ai-ctx__gate">
        <p class="croc-ai-ctx__k">Tonight's next step</p>
        ${decisionSurfaceActionsHtml({
          incidentId: leadId,
          context: "home",
          compact: true,
          actions: advertised,
        })}
      </div>`
    : `<div class="croc-ai-ctx__gate">
        <p class="croc-ai-ctx__k">Tonight's next step</p>
        <p class="muted croc-ai-ctx__quiet">Nothing is waiting. Hold · Takeover · More evidence appear only when the Watch Needs you.</p>
      </div>`;

  return `
  <section class="croc-ai-ctx glass-panel glass-panel--panel" data-ai-context="1" data-ai-ctx-pulse="${escapeHtml(pulse)}" aria-label="More about tonight">
    <header class="croc-ai-ctx__head">
      <div>
        <p class="croc-ai-ctx__eyebrow">More about tonight</p>
        <h3 class="croc-ai-ctx__title">What the Watch can see</h3>
        <p class="muted croc-ai-ctx__lede">The map stays large. A next step appears only when someone must interrupt.</p>
      </div>
      ${modelBadge}
    </header>
    <div class="croc-as-pulse croc-ai-ctx__pulse" data-as-pulse="${escapeHtml(pulse)}" aria-live="polite">
      <div class="croc-as-pulse__stages" role="list">
        <span class="croc-as-pulse__stage${pulse === "watching" ? " is-lit" : ""}" role="listitem"><i aria-hidden="true"></i>Watching</span>
        <span class="croc-as-pulse__stage${pulse === "acting" ? " is-lit" : ""}" role="listitem"><i aria-hidden="true"></i>Acting</span>
        <span class="croc-as-pulse__stage${pulse === "interrupt" ? " is-lit" : ""}" role="listitem"><i aria-hidden="true"></i>Needs you</span>
      </div>
      <p class="croc-as-pulse__line">${escapeHtml(pulseLine)}</p>
    </div>
    <div class="croc-ai-ctx__why">
      <p class="croc-ai-ctx__k">Why</p>
      <p class="croc-ai-ctx__why-body">${escapeHtml(whyLine)}</p>
    </div>
    ${aiOn
      ? maicActionWorkbenchHtml({
          waitingN,
          category: fields.category,
          riskScore: fields.riskScore,
          reasonLine: fields.reasonLine,
          responderHint: fields.responderHint,
        })
      : `<p class="muted croc-ai-ctx__quiet">Cards open for staff when policy is on for this site.</p>`}
    ${decisionBlock}
    <div class="croc-ai-ctx__next row" style="gap:8px;flex-wrap:wrap;margin-top:10px">
      ${leadId
        ? `<button type="button" class="btn sm btn-tap" data-ai-map-focus="${escapeHtml(leadId)}" data-ai-map-tab="observation">Show on map</button>`
        : `<button type="button" class="btn sm btn-tap" data-cc-go="overview">Show on map</button>`}
      <button type="button" class="btn sm secondary btn-tap" data-cc-go="incidents">Open cases</button>
      ${isAdmin ? `<button type="button" class="btn sm ghost btn-tap" data-cc-go="auto-sentinel">Policy</button>` : ""}
    </div>
    ${capabilityRegistrySoftHtml({
      settings: pack.settings,
      modelUsed,
      compact: true,
      softLanes: pack.softLanes || null,
    })}
    <p class="honest croc-ai-ctx__note">Notify first. Rules keep the score. A short why may appear. Pictures do not change the score.</p>
  </section>`;
}
